import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    LayoutDashboard, Target, Paperclip, BookOpen, TrendingUp,
    ClipboardList, BarChart3, Building2, Users, Camera, Download,
    Mail, Phone, Briefcase, MapPin, CalendarDays, FileText, ChevronDown,
    Rocket, AlertTriangle, Cake, UserRound, PartyPopper, Sparkles, CheckCircle2, XCircle, Clock, Circle,
    FileWarning, Eye, X, Trash2, ArrowRight, PenTool, Loader2, CheckCircle, ChevronLeft, ChevronRight, Bell, CheckSquare } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import MojeSmena from './MojeSmena/index.jsx';
import KalendarSmen from './prehled/KalendarSmen.jsx';
import MojeKpi from './prehled/MojeKpi.jsx';
import MojeVzdelani from './prehled/MojeVzdelani.jsx';
import { SEZNAM_SIRKA, PANEL_SIRKA, PANEL_OD } from '../components/layout/ModulLayout';
import { SKLO_SVETLE, MODAL_SKLO, MODAL_POZADI } from '../lib/uiTokens';
import useRozmer from '../hooks/useRozmer';
import { useSekce } from '../contexts/SekceContext';
import Aktivita from './Aktivita';
import MujTym from '../components/tym/MujTym';
import { REZERVA_LISTY } from '../components/MobilniLista';
import SheetModal, { SheetKarta, SheetTlacitko } from '../components/SheetModal';
import { Sun, Moon, Monitor, LogOut, Laptop } from 'lucide-react';
import ModalShell from '../components/ui/ModalShell';
import PrihlasenaZarizeni from '../components/profil/PrihlasenaZarizeni';

// Vzhled aplikace — přepínač motivu. Bydlel v listu účtu pod menu modulů,
// kam se nehodil: menu je o modulech a profil se tím dělil na dvě místa.
const MOTIVY = [
    { v: 'light', Icon: Sun, label: 'Světlý' },
    { v: 'dark', Icon: Moon, label: 'Tmavý' },
    { v: 'system', Icon: Monitor, label: 'Systém' },
];

// Mobilní sekce Přehledu (na desktopu je všechno vidět najednou v gridu).
// `moje-smena` drží id kvůli návratu z Rozvozu (`tab: 'moje-smena'`).
const ALL_TABS = [
    { id: 'moje-smena', label: 'Dnešní směna',  Icon: Clock },
    // Notifikace bydlí tady, ne na vlastní stránce: Přehled už je „co mě čeká"
    // (úkoly, podpisy, školení) a notifikace jsou totéž, jen v čase. Dvě
    // schránky, které se překrývají, nutily člověka vědět, že jsou dvě.
    { id: 'notifikace', label: 'Notifikace',    Icon: Bell },
    { id: 'profil',     label: 'Můj profil',    Icon: UserRound },
    { id: 'muj-mesic',  label: 'Můj měsíc',     Icon: CalendarDays },
    { id: 'rozvoj',     label: 'Osobní rozvoj', Icon: Rocket },
    // Jen pro vedoucí (má podřízené) a superadmina — jak si vedou moji lidé
    { id: 'muj-tym',    label: 'Můj tým',       Icon: Users },
    // Řízená dokumentace (směrnice, recepty) je od 2026-10-04 ve Znalostech
    // (slot spodní lišty) — staré odkazy přesměruje app.jsx.
];

const urovenColors = [
    'bg-gray-700 text-gray-400',
    'bg-red-500/20 text-red-400 border-red-500/30',
    'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
];
const urovenLabels = ['—', 'Zaučuje se', 'S pomocí', 'Samostatně'];
const roleLabels = { superadmin: 'Super Admin', admin: 'Admin', uzivatel: 'Uživatel' };
const roleColors = { superadmin: 'bg-amber-500/10 text-amber-400 border-amber-500/20', admin: 'bg-blue-500/10 text-blue-400 border-blue-500/20', uzivatel: 'bg-gray-500/10 text-gray-400 border-gray-500/20' };
const stavColors = { aktivni: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', neaktivni: 'bg-red-500/10 text-red-400 border-red-500/20', zkusebni: 'bg-amber-500/10 text-amber-400 border-amber-500/20', novy: 'bg-blue-500/10 text-blue-400 border-blue-500/20', vyplneno: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20', priprava_smlouvy: 'bg-purple-500/10 text-purple-400 border-purple-500/20', podpis_smlouvy: 'bg-violet-500/10 text-violet-400 border-violet-500/20', zaskoleni: 'bg-amber-500/10 text-amber-400 border-amber-500/20' };
const stavLabels = { aktivni: 'Aktivní', neaktivni: 'Neaktivní', zkusebni: 'Zkušební', novy: 'Nový', vyplneno: 'Vyplněno', priprava_smlouvy: 'Příprava smlouvy', podpis_smlouvy: 'Podpis smlouvy', zaskoleni: 'V zaškolení' };
const karieraUrovenColors = { Novice: 'bg-gray-500/10 text-gray-400 border-gray-500/20', Junior: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20', Medior: 'bg-blue-500/10 text-blue-400 border-blue-500/20', Senior: 'bg-purple-500/10 text-purple-400 border-purple-500/20', Leader: 'bg-amber-500/10 text-amber-400 border-amber-500/20' };

const SKOLENI_STAV = {
    prirazeno: { label: 'K absolvování', color: '#6b7280', Icon: AlertTriangle },
    zahajeno:  { label: 'Rozpracováno', color: '#fbbf24', Icon: Clock },
    dokonceno: { label: 'Dokončeno',    color: '#10b981', Icon: CheckCircle2 },
    neuspesne: { label: 'Neúspěšné',    color: '#f87171', Icon: XCircle },
};

// `seznam` = svislý seznam všech dokumentů po jednom na řádek (desktopový
// sloupec „Já" je úzký a scrolluje, karusel po dvou tam lámal názvy po slovech).
function DocCarousel({ label, docs, isDark, mutedColor, openDokumentPreview, seznam = false }) {
    const [idx, setIdx] = useState(0);
    const pageSize = seznam ? docs.length : 2;
    const maxIdx = Math.max(0, docs.length - pageSize);
    const visible = docs.slice(idx, idx + pageSize);
    const dnes = new Date();

    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold" style={{ color: '#F59E0B' }}>{label}</span>
                {docs.length > pageSize && (
                    <div className="flex gap-0.5">
                        <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}
                            className="p-0.5 rounded transition-colors disabled:opacity-20"
                            style={{ color: mutedColor, background: 'transparent', border: 'none', cursor: idx === 0 ? 'default' : 'pointer' }}>
                            <ChevronLeft size={14} />
                        </button>
                        <button onClick={() => setIdx(Math.min(maxIdx, idx + 1))} disabled={idx >= maxIdx}
                            className="p-0.5 rounded transition-colors disabled:opacity-20"
                            style={{ color: mutedColor, background: 'transparent', border: 'none', cursor: idx >= maxIdx ? 'default' : 'pointer' }}>
                            <ChevronRight size={14} />
                        </button>
                    </div>
                )}
            </div>
            <div className={seznam ? 'space-y-1.5' : 'grid grid-cols-2 gap-2'}>
                {visible.map(dok => {
                    const expDate = dok.expiruje_at ? new Date(dok.expiruje_at) : null;
                    const dniDo = expDate ? Math.ceil((expDate - dnes) / 86400000) : null;
                    const expStav = !expDate ? 'none' : dniDo <= 30 ? 'brzy' : 'ok';
                    const expBorder = { none: 'var(--border-default)', ok: 'var(--border-default)', brzy: 'rgba(251,191,36,0.35)' };
                    const expColor = { none: mutedColor, ok: '#34d399', brzy: '#fbbf24' };
                    const expText = !expDate ? 'Bez expirace' : `do ${expDate.toLocaleDateString('cs-CZ')}`;
                    const akce = (
                        <div className="flex items-center gap-0.5 shrink-0">
                            <button type="button" onClick={() => openDokumentPreview(dok)}
                                className="p-1 rounded transition-colors" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: mutedColor }}
                                onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'} title="Zobrazit">
                                <Eye size={11} />
                            </button>
                            <a href={`/api/dokumenty/${dok.id}/download`} target="_blank" rel="noreferrer"
                                className="p-1 rounded inline-flex" style={{ background: 'transparent', color: mutedColor, textDecoration: 'none' }}
                                onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : '#f3f4f6'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'} title="Stáhnout">
                                <Download size={11} />
                            </a>
                        </div>
                    );
                    if (seznam) {
                        return (
                            <div key={dok.id} className="flex items-center gap-2 rounded-lg pl-2.5 pr-1.5 py-1.5" style={{ border: `1px solid ${expBorder[expStav]}`, background: isDark ? 'rgba(255,255,255,0.02)' : 'var(--surface-elevated)' }}>
                                <FileText size={12} className="shrink-0" style={{ color: mutedColor }} />
                                <span className="text-[11px] font-semibold truncate flex-1 min-w-0" style={{ color: 'var(--text-primary)' }} title={dok.nazev}>{dok.nazev}</span>
                                <span className="text-[9.5px] shrink-0" style={{ color: expColor[expStav] }}>{expText}</span>
                                {akce}
                            </div>
                        );
                    }
                    return (
                        <div key={dok.id} className="rounded-lg p-2.5" style={{ border: `1px solid ${expBorder[expStav]}`, background: isDark ? 'rgba(255,255,255,0.02)' : 'var(--surface-elevated)' }}>
                            <div className="flex items-start gap-1.5">
                                <FileText size={12} className="shrink-0 mt-0.5" style={{ color: mutedColor }} />
                                <p className="text-[11px] font-semibold leading-tight flex-1 min-w-0" style={{ color: 'var(--text-primary)' }}>{dok.nazev}</p>
                                <div className="-mt-0.5 -mr-0.5">{akce}</div>
                            </div>
                            <p className="text-[9px] mt-1 ml-5" style={{ color: expColor[expStav] }}>{expText}</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Seznam aktivních osobních dokumentů seskupený podle typu (občanka, ŘP, atd.).
 * Volá se z karty Osobní (mobile uvnitř) i Dokumenty (desktop samostatná karta).
 * Filtruje expirované dokumenty a typy s `zobrazit_v_prehledu === false`.
 */
function PersonalDocsList({ zamestnanec, isDark, mutedColor, openDokumentPreview, seznam = false }) {
    const dnes = new Date();
    const allDocs = (zamestnanec.dokumenty || []).filter(dok => {
        if (!dok.expiruje_at) return true;
        return new Date(dok.expiruje_at) >= dnes;
    });
    if (allDocs.length === 0) {
        return <p className="text-xs" style={{ color: mutedColor }}>Žádné aktivní dokumenty</p>;
    }

    const groups = {};
    allDocs.forEach(dok => {
        const key = dok.typ_dokumentu_id || 'ostatni';
        const label = dok.typ_dokumentu?.nazev || 'Ostatní';
        if (!groups[key]) groups[key] = { label, docs: [], zobrazit: dok.typ_dokumentu?.zobrazit_v_prehledu !== false };
        groups[key].docs.push(dok);
    });

    const visibleGroups = Object.entries(groups).filter(([, g]) => g.zobrazit);
    if (visibleGroups.length === 0) {
        return <p className="text-xs" style={{ color: mutedColor }}>Žádné aktivní dokumenty</p>;
    }

    return (
        <div className="space-y-3">
            {visibleGroups.map(([key, group]) => (
                <DocCarousel key={key} label={group.label} docs={group.docs} isDark={isDark} mutedColor={mutedColor} openDokumentPreview={openDokumentPreview} seznam={seznam} />
            ))}
        </div>
    );
}

export default function Prehled({ user, onUserUpdate, onNavigate, onLogout, initialTab = null, navParams = null }) {
    const { theme, setTheme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    // Výchozí tab: na mobilu „Moje směna" (driver chce hned vidět cockpit), na
    // desktopu „Osobní" — na desktopu se ale zobrazují všechny sekce v gridu,
    // takže activeTab tam slouží jen jako focus indicator (skrytý tab bar).
    const [zarizeniOpen, setZarizeniOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(() => {
        const mobile = typeof window !== 'undefined' && window.innerWidth < 768;
        return initialTab || (mobile ? 'moje-smena' : 'profil');
    });
    // Proklik zvenčí (notifikace, toast, push, slot Domů) přepne sekci i u už
    // namontované stránky — navigace na tutéž stránku ji neodmontuje. Hlídá se
    // celý objekt parametrů, ne jen `tab`: navigace vždy vyrobí nový, takže
    // druhé klepnutí na Domů vrátí na dnešní směnu, i když už tam `tab` byl.
    useEffect(() => {
        const t = navParams?.tab;
        if (t) setActiveTab(t);
    }, [navParams]);
    // Desktop = grid 2×3 layout (všechny sekce viditelné najednou). Mobile =
    // tab switcher (jedna sekce v čas). Hook musí být nahoře — Rules of Hooks
    // zakazují volat po conditional returnu (loading / error fallbacky níže).
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
    // Vejde se třetí sloupec? Stejná hranice jako ModulLayout (panelOd).
    const [triSloupce, setTriSloupce] = useState(() => typeof window !== 'undefined' && window.innerWidth >= PANEL_OD);
    useEffect(() => {
        const onResize = () => { setIsMobile(window.innerWidth < 768); setTriSloupce(window.innerWidth >= PANEL_OD); };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    // Měsíc otevřený v kalendáři směn — podle něj se počítají i KPI nad ním.
    const [kalendarMesic, setKalendarMesic] = useState(() => {
        const d = new Date();
        return { rok: d.getFullYear(), mesic: d.getMonth() + 1 };
    });
    // Výška karty „Já" — podle ní je vizitka vycentrovaná s velkou fotkou,
    // nebo (nízké okno) v jednom řádku, ať zbyde místo na úkoly a dokumenty.
    const [jaRef, jaRozmer] = useRozmer();
    const [zamestnanec, setZamestnanec] = useState(null);
    const [bonusHodnoceni, setBonusHodnoceni] = useState([]);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedHodnoceni, setExpandedHodnoceni] = useState(null);
    const [avatarUploading, setAvatarUploading] = useState(false);
    const [avatarLoadError, setAvatarLoadError] = useState(false);
    const [expandedKariera, setExpandedKariera] = useState(0);
    const [hoveredSekce, setHoveredSekce] = useState(null); // { key, rect, sekce }
    const [skoleniData, setSkoleniData] = useState([]);
    // Úkoly z Komunikace, které řeším (převzaté / zadané mně). Jen připomínka
    // v „co čeká" — ukončení směny neblokují.
    const [komunikaceUkoly, setKomunikaceUkoly] = useState([]);
    const [skoleniLoading, setSkoleniLoading] = useState(true);
    const [actionFilter, setActionFilter] = useState('vse');
    const [nepodepsaneDocs, setNepodepsaneDocs] = useState([]);
    const [nepodepsaneLoading, setNepodepsaneLoading] = useState(true);
    const [docsKPodpisu, setDocsKPodpisu] = useState([]);
    const [docsKPodpisuLoading, setDocsKPodpisuLoading] = useState(true);
    const [signModalDoc, setSignModalDoc] = useState(null);
    const [certTypy, setCertTypy] = useState([]); // [{kod, nazev, odznak, platnost_mesice}]
    const [signSubmitting, setSignSubmitting] = useState(false);
    const signCanvasRef = useRef(null);
    const signDrawingRef = useRef(false);
    const fileRef = useRef(null);

    // Náhled dokumentu na mobilu — standardní sheet (desktop: overlay níže)
    const [nahledDok, setNahledDok] = useState(null);
    const openDokumentPreview = (dok) => {
        if (window.innerWidth < 768) { setNahledDok(dok); return; }
        if (document.getElementById('dok-preview-overlay')) return;
        const mime = dok.soubor_mime || '';
        const isPdf = mime === 'application/pdf';
        const isImage = mime.startsWith('image/');
        const previewUrl = `/api/dokumenty/${dok.id}/preview`;
        const downloadUrl = `/api/dokumenty/${dok.id}/download`;
        const dark = isDark;

        // SVG ikony (lucide styl)
        const iconX = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;
        const iconDownload = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" x2="12" y1="15" y2="3"/></svg>`;
        const iconFile = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FF8A00" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/></svg>`;
        const iconAlert = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${dark ? 'rgba(255,255,255,0.3)' : '#9ca3af'}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`;

        // Formátování velikosti souboru
        const fmtSize = (b) => { if (!b) return ''; if (b < 1024) return b + ' B'; if (b < 1048576) return (b/1024).toFixed(0) + ' KB'; return (b/1048576).toFixed(1) + ' MB'; };
        const sizeText = fmtSize(dok.soubor_velikost);

        // Overlay
        const overlay = document.createElement('div');
        overlay.id = 'dok-preview-overlay';
        overlay.style.cssText = `position:fixed;top:0;left:0;width:100vw;height:100vh;z-index:99999;background:var(--modal-backdrop);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:16px;animation:dokFadeIn .2s ease;`;

        // Animace
        // Styl přepisujeme při každém otevření — hover barva závisí na tématu,
        // které se mezi otevřeními mohlo přepnout.
        let style = document.getElementById('dok-preview-style');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dok-preview-style';
            document.head.appendChild(style);
        }
        style.textContent = `
            @keyframes dokFadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes dokSlideUp { from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
            #dok-preview-overlay .dok-btn:hover { background: ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'} !important; }
        `;

        overlay.onclick = (e) => {
            if (e.target === overlay) {
                overlay.style.opacity = '0';
                overlay.style.transition = 'opacity .15s ease';
                setTimeout(() => overlay.remove(), 150);
            }
        };

        // Card
        const card = document.createElement('div');
        card.style.cssText = `background:var(--modal-bg);border:1px solid var(--modal-border);border-radius:var(--radius-xl, 8px);box-shadow:var(--modal-shadow);backdrop-filter:var(--modal-blur);-webkit-backdrop-filter:var(--modal-blur);width:100%;max-width:900px;max-height:90vh;display:flex;flex-direction:column;overflow:hidden;animation:dokSlideUp .25s ease;`;
        card.onclick = (e) => e.stopPropagation();

        const closeOverlay = () => {
            overlay.style.opacity = '0';
            overlay.style.transition = 'opacity .15s ease';
            setTimeout(() => overlay.remove(), 150);
        };

        // Escape key
        const onKey = (e) => { if (e.key === 'Escape') { closeOverlay(); document.removeEventListener('keydown', onKey); } };
        document.addEventListener('keydown', onKey);

        // Header
        const header = document.createElement('div');
        header.style.cssText = `padding:16px 20px;border-bottom:1px solid ${dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'};display:flex;align-items:center;gap:12px;`;
        header.innerHTML = `
            <div style="flex-shrink:0;display:flex;align-items:center">${iconFile}</div>
            <div style="flex:1;min-width:0">
                <p style="font-size:15px;font-weight:600;color:${dark ? 'rgba(255,255,255,0.95)' : '#111827'};margin:0;line-height:1.3">${dok.nazev}</p>
                <p style="font-size:11px;color:${dark ? 'rgba(255,255,255,0.4)' : '#9ca3af'};margin:3px 0 0;line-height:1.2">${dok.soubor_nazev || ''}${sizeText ? ' · ' + sizeText : ''}</p>
            </div>`;
        const closeBtn = document.createElement('button');
        closeBtn.className = 'dok-btn';
        closeBtn.innerHTML = iconX;
        closeBtn.style.cssText = `width:32px;height:32px;border-radius:6px;border:none;cursor:pointer;background:${dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};color:${dark ? 'rgba(255,255,255,0.5)' : '#6b7280'};display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .15s;`;
        closeBtn.onclick = closeOverlay;
        header.appendChild(closeBtn);

        // Body
        const body = document.createElement('div');
        body.style.cssText = `flex:1;overflow:auto;`;
        if (isPdf && window.innerWidth >= 768) {
            body.innerHTML = `<iframe src="${previewUrl}" style="width:100%;height:70vh;border:none;display:block"></iframe>`;
        } else if (isImage) {
            body.style.cssText += `padding:24px;display:flex;align-items:center;justify-content:center;background:${dark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)'};`;
            body.innerHTML = `<img src="${previewUrl}" style="max-width:100%;max-height:70vh;object-fit:contain;border-radius:6px;box-shadow:0 4px 20px rgba(0,0,0,${dark ? '0.3' : '0.1'})" />`;
        } else {
            body.style.cssText += `padding:48px 24px;text-align:center;`;
            body.innerHTML = `
                <div style="margin:0 auto 16px;display:flex;justify-content:center">${iconAlert}</div>
                <p style="font-size:14px;font-weight:500;color:${dark ? 'rgba(255,255,255,0.85)' : '#111827'};margin:0 0 6px">
                    ${isPdf && window.innerWidth < 768 ? 'Na mobilním zařízení nelze zobrazit PDF přímo.' : 'Tento typ souboru nelze zobrazit v prohlížeči.'}
                </p>
                <p style="font-size:13px;color:${dark ? 'rgba(255,255,255,0.4)' : '#9ca3af'};margin:0">
                    Stáhněte soubor a otevřete ho ve vhodné aplikaci.
                </p>`;
        }

        // Footer
        const footer = document.createElement('div');
        footer.style.cssText = `padding:14px 20px;border-top:1px solid ${dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'};display:flex;align-items:center;gap:8px;justify-content:flex-end;`;

        const dlBtn = document.createElement('a');
        dlBtn.href = downloadUrl;
        dlBtn.className = 'dok-btn';
        dlBtn.innerHTML = `${iconDownload} <span>Stáhnout</span>`;
        dlBtn.style.cssText = `display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:6px;font-size:13px;font-weight:500;text-decoration:none;cursor:pointer;background:${dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};color:${dark ? 'rgba(255,255,255,0.7)' : '#374151'};border:1px solid ${dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};transition:background .15s;`;

        const closeBtn2 = document.createElement('button');
        closeBtn2.className = 'dok-btn';
        closeBtn2.textContent = 'Zavřít';
        closeBtn2.style.cssText = `padding:8px 16px;border-radius:6px;font-size:13px;font-weight:500;border:1px solid ${dark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'};background:${dark ? 'rgba(255,255,255,0.04)' : 'transparent'};cursor:pointer;color:${dark ? 'rgba(255,255,255,0.55)' : '#6b7280'};transition:background .15s;`;
        closeBtn2.onclick = closeOverlay;

        footer.append(dlBtn, closeBtn2);
        card.append(header, body, footer);
        overlay.appendChild(card);
        document.body.appendChild(overlay);
    };

    // Můj tým (2026-10-11) vidí jen ten, kdo má podřízené (superadmin vždy).
    // Na mobilu je to sekce, na desktopu přepínač „Já | Můj tým" nad mřížkou.
    // Přepínání přejetím prstem po ploše je pryč — pralo se s vodorovnými
    // gesty uvnitř sekcí (cockpit, kalendář). Sekce se mění jen v liště.
    const maTym = !!user?.has_subordinates || user?.role === 'superadmin';
    const tabs = maTym ? ALL_TABS : ALL_TABS.filter(t => t.id !== 'muj-tym');
    const [deskPohled, setDeskPohled] = useState(() => (initialTab === 'muj-tym' && maTym ? 'tym' : 'ja'));
    useEffect(() => { if (navParams?.tab === 'muj-tym' && maTym) setDeskPohled('tym'); }, [navParams, maTym]);
    // Sekce přepíná střed mobilní lišty. Notifikace se do sloupce nekreslí
    // (`skryta`) — mají v liště vlastní slot a byly by tam podruhé. Ze
    // seznamu ale zmizet nesmí: střed v něm hledá název otevřené sekce, a bez
    // záznamu spadl na název stránky, takže v Notifikacích tvrdil „Přehled".
    // Profil ve vysunutém sloupci nese profilovou fotku místo ikony — mezi
    // pěti stejnými ikonami se jinak hledá, a je to jediná sekce, která patří
    // člověku, ne práci.
    const sekceStredu = tabs.map(t => {
        if (t.id === 'notifikace') return { ...t, skryta: true };
        if (t.id === 'profil' && avatarUrl && !avatarLoadError) return { ...t, avatar: avatarUrl };
        return t;
    });
    useSekce(isMobile ? { sekce: sekceStredu, aktivni: activeTab, onVyber: setActiveTab } : null);

    // Předchozí logika fetchovala /smena/dnes při mountu a přepínala na
    // moje-smena pokud měl driver dnes směnu. Teď to řešíme přímo v initial
    // state activeTab (mobile = moje-smena always, desktop = osobni).

    useEffect(() => {
        fetch('/api/certifikace').then(r => r.json()).then(data => {
            setCertTypy(Array.isArray(data) ? data : []);
        }).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        fetch('/api/me/zamestnanec')
            .then(r => {
                if (!r.ok) throw new Error(r.status === 404 ? 'not_linked' : 'error');
                return r.json();
            })
            .then(data => {
                setZamestnanec(data.zamestnanec);
                setBonusHodnoceni(data.bonus_hodnoceni || []);
                setAvatarUrl(data.avatar_url);
                setLoading(false);
            })
            .catch(err => {
                setError(err.message === 'not_linked' ? 'not_linked' : 'error');
                setLoading(false);
            });
        const nactiUkoly = () => fetch('/api/ukoly/moje').then(r => (r.ok ? r.json() : null))
            .then(d => d && setKomunikaceUkoly(Array.isArray(d.ukoly) ? d.ukoly : [])).catch(() => {});
        nactiUkoly();
        window.addEventListener('app-resumed', nactiUkoly);
        fetch('/api/moje-skoleni').then(r => r.json()).then(data => {
            setSkoleniData(data);
            setSkoleniLoading(false);
        }).catch(() => setSkoleniLoading(false));
        nactiNepodepsane();
        fetch('/api/me/dokumenty-k-podpisu').then(r => r.json()).then(data => {
            setDocsKPodpisu(data);
            setDocsKPodpisuLoading(false);
        }).catch(() => setDocsKPodpisuLoading(false));
        // Úkol se odškrtává v Komunikaci — po návratu do aplikace (a při přepnutí
        // zpět na Přehled) ať tu nevisí hotový
        window.addEventListener('focus', nactiUkoly);
        return () => {
            window.removeEventListener('app-resumed', nactiUkoly);
            window.removeEventListener('focus', nactiUkoly);
        };
    }, []);

    useEffect(() => { setAvatarLoadError(false); }, [avatarUrl]);

    // Potvrzení v Dokumentaci ubere úkol k podpisu — seznam úkolů se musí srovnat
    function nactiNepodepsane() {
        fetch('/api/bezpecnost/smernice/nepodepsane').then(r => r.json()).then(data => {
            setNepodepsaneDocs(data);
            setNepodepsaneLoading(false);
        }).catch(() => setNepodepsaneLoading(false));
    }

    const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAvatarUploading(true);
        try {
            const fd = new FormData();
            fd.append('avatar', file);
            const res = await fetch('/auth/avatar', {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': csrf() },
                body: fd,
            });
            const data = await res.json();
            if (res.ok) {
                setAvatarUrl(data.avatar_url);
                onUserUpdate?.({ ...user, avatar_url: data.avatar_url });
            }
        } finally {
            setAvatarUploading(false);
            e.target.value = '';
        }
    };

    const handleAvatarDelete = async () => {
        if (!confirm('Odebrat vlastní fotografii?')) return;
        setAvatarUploading(true);
        try {
            const res = await fetch('/auth/avatar', {
                method: 'DELETE',
                headers: { 'X-CSRF-TOKEN': csrf() },
            });
            const data = await res.json();
            if (res.ok) {
                setAvatarUrl(data.avatar_url);
                onUserUpdate?.({ ...user, avatar_url: data.avatar_url });
            }
        } finally {
            setAvatarUploading(false);
        }
    };

    const cardStyle = isDark ? {
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 0 0 0.5px rgba(255,255,255,0.03)',
    } : SKLO_SVETLE;
    // Mobil „Můj měsíc": výraznější sklo (jako hlavička a Palec) pro kalendář i KPI
    const skloMesic = {
        ...(isDark ? {
            background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.035) 100%)',
            border: '1px solid rgba(255,255,255,0.13)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.08)',
        } : {
            background: 'linear-gradient(180deg, rgba(255,255,255,0.62) 0%, rgba(255,255,255,0.40) 100%)',
            border: '1px solid rgba(255,255,255,0.8)',
            boxShadow: '0 10px 30px rgba(31,38,90,0.10), inset 0 1px 0 rgba(255,255,255,0.7)',
        }),
        backdropFilter: 'blur(22px) saturate(170%)',
        WebkitBackdropFilter: 'blur(22px) saturate(170%)',
    };
    // Měsíc otevřený v kalendáři jako období KPI ('YYYY-MM')
    const obdobiKalendare = `${kalendarMesic.rok}-${String(kalendarMesic.mesic).padStart(2, '0')}`;

    // Desktop: každý blok cockpitu má pevnou výšku dle řádku gridu a scrolluje
    // uvnitř. Rozbalení obsahu (kvalifikační cíle, seznam akcí…) tak nepřepočítá
    // layout a ostatní bloky „neskočí". Na mobilu scrolluje tab kontejner.
    const deskCell = (area) => (isMobile ? undefined : { gridArea: area, minHeight: 0, overflow: 'hidden' });
    const deskCard = isMobile
        ? cardStyle
        : { ...cardStyle, height: '100%', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' };

    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted)';
    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';
    const borderSubtle = isDark ? 'rgba(255,255,255,0.04)' : 'var(--border-subtle)';

    // ── Signing modal canvas helpers (must be before early returns to satisfy Rules of Hooks) ──
    const initSignCanvas = useCallback((canvas) => {
        if (!canvas) return;
        signCanvasRef.current = canvas;
        const ctx = canvas.getContext('2d');
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = isDark ? '#fff' : '#1a1a2e';

        const getPos = (e) => {
            const rect = canvas.getBoundingClientRect();
            const t = e.touches ? e.touches[0] : e;
            return { x: (t.clientX - rect.left) * (canvas.width / rect.width), y: (t.clientY - rect.top) * (canvas.height / rect.height) };
        };

        const start = (e) => { e.preventDefault(); signDrawingRef.current = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
        const move = (e) => { if (!signDrawingRef.current) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
        const end = () => { signDrawingRef.current = false; };

        canvas.onmousedown = start; canvas.onmousemove = move; canvas.onmouseup = end; canvas.onmouseleave = end;
        canvas.ontouchstart = start; canvas.ontouchmove = move; canvas.ontouchend = end;
    }, [isDark]);

    const clearSignCanvas = () => {
        const c = signCanvasRef.current;
        if (c) c.getContext('2d').clearRect(0, 0, c.width, c.height);
    };

    const handleSign = async () => {
        const c = signCanvasRef.current;
        if (!c || !signModalDoc) return;
        const blank = document.createElement('canvas');
        blank.width = c.width; blank.height = c.height;
        if (c.toDataURL() === blank.toDataURL()) { alert('Nakreslete svůj podpis.'); return; }
        // Export vždy s bílým pozadím a černým tahem (pro PDF)
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = c.width; exportCanvas.height = c.height;
        const exportCtx = exportCanvas.getContext('2d');
        exportCtx.drawImage(c, 0, 0);
        if (isDark) {
            const img = exportCtx.getImageData(0, 0, exportCanvas.width, exportCanvas.height);
            const d = img.data;
            for (let i = 0; i < d.length; i += 4) {
                if (d[i + 3] === 0) continue;
                d[i] = 255 - d[i];
                d[i + 1] = 255 - d[i + 1];
                d[i + 2] = 255 - d[i + 2];
            }
            exportCtx.putImageData(img, 0, 0);
        }
        exportCtx.globalCompositeOperation = 'destination-over';
        exportCtx.fillStyle = '#ffffff';
        exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
        const dataUrl = exportCanvas.toDataURL('image/png');

        setSignSubmitting(true);
        try {
            const res = await fetch(`/api/dokumenty/${signModalDoc.id}/podepsat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({ podpis: dataUrl }),
            });
            if (res.ok) {
                setDocsKPodpisu(prev => prev.filter(d => d.id !== signModalDoc.id));
                setSignModalDoc(null);
                fetch('/api/me/zamestnanec').then(r => r.json()).then(data => {
                    setZamestnanec(data.zamestnanec);
                }).catch(() => {});
            } else {
                const err = await res.json().catch(() => ({}));
                alert(err.error || 'Chyba při podepisování.');
            }
        } finally {
            setSignSubmitting(false);
        }
    };

    const headerBlock = (
        <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center justify-center shrink-0"
                style={{ width: 40, height: 40, borderRadius: 7, background: 'rgba(255,138,0,0.12)' }}>
                <LayoutDashboard size={20} style={{ color: '#FF8A00' }} />
            </div>
            <div className="min-w-0">
                <h1 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Přehled</h1>
            </div>
        </div>
    );

    if (loading) return <div>{headerBlock}<p style={{ color: mutedColor }}>Načítám...</p></div>;

    if (error === 'not_linked') {
        return (
            <div>
                {headerBlock}
                <div className="rounded-xl p-8 text-center" style={cardStyle}>
                    <Users size={40} style={{ color: mutedColor, margin: '0 auto 16px', opacity: 0.5 }} />
                    <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Profil nepřipojen</h3>
                    <p className="text-sm max-w-md mx-auto" style={{ color: mutedColor }}>
                        Váš uživatelský účet ještě nebyl propojen se zaměstnaneckým záznamem.
                        Kontaktujte svého nadřízeného nebo administrátora.
                    </p>
                </div>
            </div>
        );
    }

    if (error || !zamestnanec) {
        return (
            <div>
                {headerBlock}
                <div className="rounded-xl p-8 text-center" style={cardStyle}>
                    <p className="text-sm" style={{ color: mutedColor }}>Nepodařilo se načíst data profilu.</p>
                </div>
            </div>
        );
    }

    // Filter careers to only those directly assigned to employee
    const zamKarieraIds = new Set(
        (zamestnanec.kariery || []).map(k => k.id)
    );
    const karieraCards = zamestnanec.urovne?.filter(u => zamKarieraIds.has(u.kariera_id)).map(u => {
        const kariera = u.kariera;
        const currentUroven = u.uroven;
        const cilUroven = u.cil_uroven;
        const allUrovne = (kariera?.urovne || []).sort((a, b) => a.poradi - b.poradi);

        // Gather goal-level required dovednosti (cumulative: all levels up to goal)
        // Map: dovednost_id → max required min_uroven across all levels up to goal
        const cilPozadavkyMap = {};
        if (cilUroven) {
            allUrovne.forEach(ul => {
                if (ul.poradi <= cilUroven.poradi) {
                    ul.pozadavky?.forEach(p => {
                        cilPozadavkyMap[p.dovednost_id] = Math.max(cilPozadavkyMap[p.dovednost_id] || 0, p.min_uroven || 3);
                    });
                }
            });
        }
        const cilPozadavkyIds = new Set(Object.keys(cilPozadavkyMap).map(Number));

        // Build employee dovednosti lookup: dovednost_id → { uroven, dovednost }
        const empDovednostiMap = {};
        (zamestnanec.dovednosti || []).forEach(d => {
            const dId = d.dovednost_id || d.dovednost?.id;
            if (dId) empDovednostiMap[dId] = d;
        });

        // Build dovednost info from career pozadavky (for skills employee may not have records for)
        const pozadavkyDovednosti = {};
        if (cilUroven) {
            allUrovne.forEach(ul => {
                if (ul.poradi <= cilUroven.poradi) {
                    ul.pozadavky?.forEach(p => {
                        if (p.dovednost) pozadavkyDovednosti[p.dovednost_id] = p.dovednost;
                    });
                }
            });
        }

        // Group by sekce — only skills required for target level
        const sekceBySekceId = {};
        Object.entries(cilPozadavkyMap).forEach(([dovednostIdStr, requiredUroven]) => {
            const dovednostId = Number(dovednostIdStr);
            const empRecord = empDovednostiMap[dovednostId];
            const dovednost = empRecord?.dovednost || pozadavkyDovednosti[dovednostId];
            if (!dovednost) return;
            const sekce = dovednost.sekce;
            if (!sekce) return;
            const uroven = empRecord?.uroven || 0;
            if (!sekceBySekceId[sekce.id]) {
                sekceBySekceId[sekce.id] = { nazev: sekce.nazev, ikona: sekce.ikona, dovednosti: [] };
            }
            sekceBySekceId[sekce.id].dovednosti.push({
                id: dovednostId,
                kod: dovednost.kod,
                nazev: dovednost.nazev,
                uroven,
                requiredUroven,
                done: uroven >= requiredUroven,
            });
        });

        // Calculate goal progress
        const cilTotal = cilPozadavkyIds.size;
        let cilDone = 0;
        Object.values(sekceBySekceId).forEach(sekce => {
            cilDone += sekce.dovednosti.filter(d => d.done).length;
        });
        const cilPct = cilTotal > 0 ? Math.round((cilDone / cilTotal) * 100) : null;

        // Rate difference
        const currentSazba = u.sazba_kc_h || 0;
        const cilUrovenObj = cilUroven ? allUrovne.find(ul => ul.id === cilUroven.id) : null;
        const cilSazba = cilUrovenObj?.sazba_kc_h || null;
        const sazbaDiff = cilSazba ? cilSazba - currentSazba : null;

        return {
            kariera, currentUroven, cilUroven, allUrovne,
            sazbaKcH: currentSazba, cilSazba, sazbaDiff,
            certOk: u.cert_ok,
            sekceBySekceId, cilPct, cilTotal, cilDone,
        };
    }) || [];

    // ── Build unified action items for "Akce k dokončení" ──
    const MESICE = ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'];
    const buildActionItems = () => {
        const now = new Date();
        const items = [];
        // Směrnice se čtou a potvrzují ve Znalostech — proklik otevře rovnou
        // ten dokument (modul Směrnice zanikl 2026-10-11).
        nepodepsaneDocs.forEach(doc => {
            const kategorie = doc.kategorie?.nazev || (typeof doc.kategorie === 'string' ? doc.kategorie : null) || 'Směrnice';
            items.push({
                type: 'dokument', id: `doc-${doc.id}`,
                title: doc.nazev,
                subtitle: `${kategorie} · v${doc.aktualni_verze?.cislo_verze || 1}`,
                deadline: null,
                icon: FileText, iconColor: '#f97316',
                onClick: () => onNavigate('znalosti-seznam', { druh: 'smernice', smernice: doc.id }),
            });
        });
        docsKPodpisu.forEach(doc => {
            items.push({
                type: 'dokument-podpis', id: `dpod-${doc.id}`,
                title: doc.nazev,
                subtitle: doc.typ === 'smlouva' ? 'Smlouva k podpisu' : 'Dokument k podpisu',
                deadline: zamestnanec?.datum_nastupu ? new Date(zamestnanec.datum_nastupu) : null,
                icon: PenTool, iconColor: '#a855f7',
                onClick: () => setSignModalDoc(doc),
            });
        });
        skoleniData.filter(s => s.stav !== 'dokonceno').forEach(s => {
            const termin = s.prirazeni?.termin_do ? new Date(s.prirazeni.termin_do) : null;
            const cfg = SKOLENI_STAV[s.stav] || SKOLENI_STAV.prirazeno;
            items.push({
                type: 'skoleni', id: `sk-${s.id}`,
                title: s.skoleni?.nazev,
                subtitle: `${s.skoleni?.sekce?.length || 0} sekcí · ${cfg.label}`,
                deadline: termin,
                icon: BookOpen, iconColor: '#B47AEA',
                onClick: () => onNavigate?.('moje-skoleni-detail', { skoleniId: s.id }),
                progressPct: s.progress_pct, stav: s.stav,
            });
        });
        // Úkoly a hlášení z Komunikace, které řeším — dokud nejsou hotové
        komunikaceUkoly.forEach(u => {
            items.push({
                type: 'ukol', id: `uk-${u.id}`,
                title: u.nazev,
                subtitle: [u.druh_nazev, u.nalehave ? 'naléhavé' : null, u.zalozil && u.zalozil !== `${zamestnanec?.jmeno} ${zamestnanec?.prijmeni}` ? `od ${u.zalozil}` : null].filter(Boolean).join(' · '),
                deadline: u.termin ? new Date(u.termin) : null,
                icon: CheckSquare, iconColor: '#B47AEA',
                onClick: () => onNavigate?.('zpravy', { tab: 'ukoly', ukolId: u.id }),
            });
        });
        items.sort((a, b) => {
            if (a.deadline && b.deadline) return a.deadline - b.deadline;
            if (a.deadline) return -1;
            if (b.deadline) return 1;
            return 0;
        });
        const today = new Date(); today.setHours(23, 59, 59, 999);
        const weekEnd = new Date(now.getTime() + 7 * 86400000);
        return {
            dnes: items.filter(i => i.deadline && i.deadline <= today),
            doTydne: items.filter(i => i.deadline && i.deadline > today && i.deadline <= weekEnd),
            dlouhodobe: items.filter(i => !i.deadline || i.deadline > weekEnd),
            all: items,
        };
    };
    const actionItems = buildActionItems();
    // Certifikáty, které kariéra vyžaduje a chybí, propadly nebo do 30 dnů propadnou
    const certifikatyKReseni = (() => {
        const pozadovane = [...new Set(karieraCards.flatMap(kc => kc.kariera?.pozadovane_certifikace || []))];
        const za30dni = new Date(Date.now() + 30 * 86400000);
        return pozadovane.filter(typ => {
            const cert = zamestnanec.certifikace?.find(c => c.typ === typ);
            return !cert || new Date(cert.platnost_do) < za30dni;
        }).length;
    })();
    const filteredActions = actionFilter === 'vse' ? actionItems.all
        : actionFilter === 'dnes' ? actionItems.dnes
        : actionFilter === 'doTydne' ? actionItems.doTydne
        : actionItems.dlouhodobe;
    const actionFilters = [
        { id: 'vse', label: 'Vše', count: actionItems.all.length },
        { id: 'dnes', label: 'Dnes', count: actionItems.dnes.length },
        { id: 'doTydne', label: 'Do týdne', count: actionItems.doTydne.length },
        { id: 'dlouhodobe', label: 'Dlouhodobé', count: actionItems.dlouhodobe.length },
    ];

    const deadlineBadge = (deadline) => {
        if (!deadline) return null;
        const now = new Date();
        const diff = Math.ceil((deadline - now) / 86400000);
        if (diff < 0) return { text: 'Po termínu!', color: '#f87171', bg: 'rgba(248,113,113,0.12)' };
        if (diff === 0) return { text: 'Dnes', color: '#f87171', bg: 'rgba(248,113,113,0.12)' };
        if (diff <= 7) return { text: `Do ${diff} dní`, color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' };
        if (diff <= 30) return { text: `Do ${diff} dní`, color: secondaryColor, bg: isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-elevated)' };
        return { text: `Do ${diff} dní`, color: mutedColor, bg: 'transparent' };
    };

    // isMobile/showSection helpers přesunuté nahoru k ostatním hookům — viz
    // sekce useState přímo pod export default function.
    const showSection = (id) => !isMobile || activeTab === id;

    // Seznam úkolů (akce k dokončení) — kreslí ho mobilní tab „Akce" i desktopový
    // sloupec „Já", ať vypadají stejně.
    // Fotka ve vizitce desktopového Přehledu. Při najetí myší nabídne nahrání
    // a odebrání fotky; tlačítka rostou s fotkou.
    const renderVizitkaFoto = (velikost, zaobleni) => {
        const tlacitko = velikost >= 80 ? 30 : 24;
        const ikona = velikost >= 80 ? 14 : 12;
        return (
            <div className="relative group shrink-0" style={{ width: velikost, height: velikost }}>
                <div className="w-full h-full overflow-hidden" style={{
                    borderRadius: zaobleni,
                    boxShadow: velikost >= 80
                        ? `0 0 0 3px ${isDark ? 'rgba(255,138,0,0.38)' : 'rgba(255,138,0,0.32)'}, 0 12px 30px -10px rgba(0,0,0,${isDark ? 0.7 : 0.3})`
                        : `0 0 0 2px ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                }}>
                    {avatarUrl && !avatarLoadError ? (
                        <img src={avatarUrl} className="w-full h-full object-cover object-center" alt=""
                            onError={() => setAvatarLoadError(true)} />
                    ) : (
                        <div className="w-full h-full bg-amber-500/20 flex items-center justify-center text-amber-500 font-bold"
                            style={{ fontSize: Math.round(velikost * 0.34) }}>
                            {zamestnanec.jmeno?.[0]}{zamestnanec.prijmeni?.[0]}
                        </div>
                    )}
                </div>
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ borderRadius: zaobleni }}>
                    <button onClick={() => fileRef.current?.click()} disabled={avatarUploading}
                        className="rounded-full bg-white/20 flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors"
                        style={{ width: tlacitko, height: tlacitko }}
                        title="Nahrát fotografii">
                        <Camera size={ikona} className="text-white" />
                    </button>
                    {avatarUrl && !avatarLoadError && (
                        <button onClick={handleAvatarDelete} disabled={avatarUploading}
                            className="rounded-full bg-white/20 flex items-center justify-center cursor-pointer hover:bg-red-500/40 transition-colors"
                            style={{ width: tlacitko, height: tlacitko }}
                            title="Odebrat fotografii">
                            <Trash2 size={ikona} className="text-white" />
                        </button>
                    )}
                </div>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAvatarChange} />
            </div>
        );
    };

    const renderAkceItems = (items) => items.map(item => {
        const badge = deadlineBadge(item.deadline);
        return (
            <div key={item.id} onClick={item.onClick || undefined}
                className={`flex items-start gap-3 rounded-lg px-3 py-2.5 transition-all${item.onClick ? ' cursor-pointer' : ''}`}
                style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'var(--surface-elevated)', border: `1px solid ${borderColor}` }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--brand)'} onMouseLeave={e => e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}>
                <item.icon size={16} className="shrink-0 mt-0.5" style={{ color: item.iconColor }} />
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{item.title}</p>
                    <p className="text-[10px] mt-0.5 truncate" style={{ color: mutedColor }}>{item.subtitle}</p>
                    {item.progressPct != null && item.stav !== 'prirazeno' && (
                        <div className="h-1 rounded-full overflow-hidden mt-1.5" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)' }}>
                            <div className="h-full rounded-full" style={{ width: `${item.progressPct}%`, background: 'var(--brand)' }} />
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                    {badge && <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded" style={{ color: badge.color, background: badge.bg }}>{badge.text}</span>}
                    <span className="text-[10px] flex items-center gap-0.5" style={{ color: 'var(--brand)' }}>Otevřít <ArrowRight size={10} /></span>
                </div>
            </div>
        );
    });

    // Stav nástupu (příprava smlouvy / zaškolení). Dřív jen na mobilu — nový
    // člověk na desktopu neviděl, v jakém stavu je a co se od něj čeká.
    const renderOnboardingBanner = (kompaktni = false) => {
        if (!['priprava_smlouvy', 'zaskoleni'].includes(zamestnanec.stav)) return null;
        const zaskoleni = zamestnanec.stav === 'zaskoleni';
        return (
            <div className={`rounded-xl ${kompaktni ? 'px-3.5 py-3' : 'p-5'}`} style={{
                ...cardStyle,
                borderColor: zaskoleni ? 'rgba(245,158,11,0.3)' : 'rgba(168,85,247,0.3)',
            }}>
                <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: zaskoleni ? '#F59E0B' : '#a855f7' }}>
                        {zaskoleni ? <Rocket size={14} /> : <FileText size={14} />}
                        {zaskoleni ? 'Zaškolení' : 'Příprava smlouvy'}
                    </h3>
                    {zamestnanec.onboarding_deadline && (() => {
                        const daysLeft = Math.ceil((new Date(zamestnanec.onboarding_deadline) - new Date()) / 86400000);
                        return (
                            <span className={`text-xs font-semibold shrink-0 ${daysLeft <= 0 ? 'text-red-400' : daysLeft <= 2 ? 'text-amber-400' : 'text-emerald-400'}`}>
                                {new Date(zamestnanec.onboarding_deadline).toLocaleDateString('cs-CZ')}
                                {daysLeft > 0 ? ` (${daysLeft} dní)` : ' (po termínu!)'}
                            </span>
                        );
                    })()}
                </div>
                <p className={`text-xs ${kompaktni ? 'mt-1' : 'mt-2'}`} style={{ color: mutedColor }}>
                    {zaskoleni
                        ? 'Dokončete certifikace a podepište směrnice pro aktivaci účtu.'
                        : 'Čeká se na nahrání podepsané smlouvy.'}
                </p>
            </div>
        );
    };

    return (
        <div style={isMobile
            ? { display: 'flex', flexDirection: 'column', height: '100%' }
            : { display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            {/* ── Podpis dokumentu ── mobil: standardní sheet, desktop: modál */}
            {signModalDoc && (() => {
                const hranaPodpisu = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';
                const stavPodpisu = signModalDoc.vyzaduje_podpis && (
                    signModalDoc.podpis_zamestnavatel_at ? (
                        <div className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold" style={{ color: '#10b981' }}>
                            <CheckCircle size={12} />
                            {'Podepsáno zaměstnavatelem: '}
                            {new Date(signModalDoc.podpis_zamestnavatel_at).toLocaleDateString('cs-CZ')}
                            {signModalDoc.podpis_zamestnavatel && ` — ${signModalDoc.podpis_zamestnavatel.jmeno} ${signModalDoc.podpis_zamestnavatel.prijmeni}`}
                        </div>
                    ) : (
                        <div className="mt-1.5 flex items-center gap-1 text-[11px]" style={{ color: '#9ca3af' }}>
                            <Clock size={12} />
                            Čeká na podpis zaměstnavatele
                        </div>
                    )
                );
                const platno = (
                    <canvas ref={initSignCanvas} width={500} height={120} style={{ width: '100%', height: isMobile ? 150 : 120, touchAction: 'none', cursor: 'crosshair', borderRadius: 6 }} />
                );

                if (isMobile) {
                    return (
                        <SheetModal onClose={() => setSignModalDoc(null)} isDark={isDark}
                            ikona={PenTool} barva="#A855F7"
                            nadpis="Podpis dokumentu" podnadpis={signModalDoc.nazev}
                            zavritLze={!signSubmitting} zIndex={99999}
                            paticka={(
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <SheetTlacitko isDark={isDark} onClick={clearSignCanvas} style={{ flex: '0 0 auto', width: 'auto', padding: '0 16px' }}>
                                        Smazat
                                    </SheetTlacitko>
                                    <SheetTlacitko isDark={isDark} hlavni barva="#A855F7" onClick={handleSign} disabled={signSubmitting}
                                        style={{ flex: 1, opacity: signSubmitting ? 0.7 : 1, cursor: signSubmitting ? 'not-allowed' : 'pointer' }}>
                                        {signSubmitting ? <><Loader2 size={16} className="animate-spin" /> Podepisuji…</> : <><PenTool size={16} /> Podepsat a potvrdit</>}
                                    </SheetTlacitko>
                                </div>
                            )}>
                            <SheetKarta isDark={isDark} style={{ padding: '12px 14px' }}>
                                <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)', margin: 0 }}>{signModalDoc.nazev}</p>
                                {signModalDoc.popis && <p className="text-xs mt-1" style={{ color: mutedColor, margin: 0 }}>{signModalDoc.popis}</p>}
                                {stavPodpisu}
                                <a href={`/api/dokumenty/${signModalDoc.id}/download`} className="text-xs mt-2.5 inline-flex items-center gap-1.5 font-semibold" style={{ color: '#a855f7' }}>
                                    <Download size={13} /> {signModalDoc.soubor_mime === 'application/pdf' ? 'Otevřít dokument ke čtení' : 'Stáhnout dokument'}
                                </a>
                            </SheetKarta>
                            <SheetKarta isDark={isDark} style={{ padding: '12px 14px' }}>
                                <p className="text-[11px] font-semibold mb-2" style={{ color: secondaryColor, margin: '0 0 8px' }}>Váš podpis</p>
                                <div style={{ border: `1px dashed ${isDark ? 'rgba(255,255,255,0.18)' : 'var(--border-default)'}`, borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.02)' : 'var(--surface-elevated)', padding: 2 }}>
                                    {platno}
                                </div>
                            </SheetKarta>
                        </SheetModal>
                    );
                }

                return (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 99999, ...MODAL_POZADI, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                    onClick={(e) => { if (e.target === e.currentTarget && !signSubmitting) setSignModalDoc(null); }}>
                    <div style={{ ...MODAL_SKLO, width: '100%', maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                        onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: `1px solid ${hranaPodpisu}` }}>
                            <div className="flex items-center gap-2">
                                <PenTool size={16} style={{ color: '#a855f7' }} />
                                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Podpis dokumentu</span>
                            </div>
                            <button onClick={() => !signSubmitting && setSignModalDoc(null)} className="p-1 rounded-lg transition-colors" style={{ color: mutedColor, cursor: signSubmitting ? 'not-allowed' : 'pointer' }}>
                                <X size={18} />
                            </button>
                        </div>
                        {/* Dokument info */}
                        <div className="px-5 py-3" style={{ borderBottom: `1px solid ${borderColor}` }}>
                            <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{signModalDoc.nazev}</p>
                            {signModalDoc.popis && <p className="text-[11px] mt-0.5" style={{ color: mutedColor }}>{signModalDoc.popis}</p>}
                            {stavPodpisu}
                        </div>
                        {/* PDF preview */}
                        <div style={{ flex: 1, minHeight: 0, maxHeight: 400, overflow: 'hidden' }}>
                            {signModalDoc.soubor_mime === 'application/pdf' ? (
                                <iframe src={`/api/dokumenty/${signModalDoc.id}/preview`} style={{ width: '100%', height: '100%', border: 'none', minHeight: 350 }} title="Náhled dokumentu" />
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full py-6" style={{ color: mutedColor }}>
                                    <FileWarning size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
                                    <p className="text-xs">Náhled tohoto typu souboru není podporován.</p>
                                    <a href={`/api/dokumenty/${signModalDoc.id}/download`} className="text-xs mt-2 flex items-center gap-1" style={{ color: '#a855f7' }}>
                                        <Download size={12} /> Stáhnout dokument
                                    </a>
                                </div>
                            )}
                        </div>
                        {/* Podpis canvas */}
                        <div className="px-5 py-4" style={{ borderTop: `1px solid ${hranaPodpisu}` }}>
                            <p className="text-[11px] font-semibold mb-2" style={{ color: secondaryColor }}>Váš podpis</p>
                            <div style={{ border: `1px dashed ${isDark ? 'rgba(255,255,255,0.15)' : 'var(--border-default)'}`, borderRadius: 6, background: isDark ? 'rgba(255,255,255,0.02)' : 'var(--surface-elevated)', padding: 2 }}>
                                {platno}
                            </div>
                            <div className="flex items-center justify-between mt-3">
                                <button onClick={clearSignCanvas} className="text-[11px] px-3 py-1.5 rounded-lg transition-colors"
                                    style={{ color: mutedColor, border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`, background: 'transparent', cursor: 'pointer' }}>
                                    Smazat podpis
                                </button>
                                <button onClick={handleSign} disabled={signSubmitting}
                                    className="text-[11px] font-semibold px-5 py-1.5 rounded-lg transition-colors flex items-center gap-1.5"
                                    style={{ background: '#a855f7', color: '#fff', border: 'none', cursor: signSubmitting ? 'not-allowed' : 'pointer', opacity: signSubmitting ? 0.7 : 1 }}>
                                    {signSubmitting ? <><Loader2 size={12} className="animate-spin" /> Podepisuji…</> : <><PenTool size={12} /> Podepsat a potvrdit</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* ── Náhled dokumentu (mobil) ── */}
            {nahledDok && (() => {
                const mime = nahledDok.soubor_mime || '';
                const velikost = nahledDok.soubor_velikost;
                const velikostText = !velikost ? '' : velikost < 1048576 ? `${Math.round(velikost / 1024)} KB` : `${(velikost / 1048576).toFixed(1)} MB`;
                return (
                    <SheetModal onClose={() => setNahledDok(null)} isDark={isDark}
                        ikona={FileText} barva="#FF8A00"
                        nadpis={nahledDok.nazev}
                        podnadpis={[nahledDok.soubor_nazev, velikostText].filter(Boolean).join(' · ') || null}
                        paticka={(
                            <SheetTlacitko isDark={isDark} hlavni as="a" href={`/api/dokumenty/${nahledDok.id}/download`}>
                                <Download size={16} /> {mime === 'application/pdf' ? 'Otevřít dokument' : 'Stáhnout'}
                            </SheetTlacitko>
                        )}>
                        {mime.startsWith('image/') ? (
                            <SheetKarta isDark={isDark} style={{ padding: 8, display: 'flex', justifyContent: 'center' }}>
                                <img src={`/api/dokumenty/${nahledDok.id}/preview`} alt={nahledDok.nazev}
                                    style={{ maxWidth: '100%', maxHeight: '55dvh', objectFit: 'contain', borderRadius: 8 }} />
                            </SheetKarta>
                        ) : (
                            <SheetKarta isDark={isDark} style={{ padding: '28px 16px', textAlign: 'center' }}>
                                <FileText size={36} style={{ color: mutedColor, opacity: 0.6, margin: '0 auto 10px' }} />
                                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)', margin: '0 0 4px' }}>
                                    {mime === 'application/pdf' ? 'PDF se otevře v prohlížeči dokumentů.' : 'Tento typ souboru nejde zobrazit přímo.'}
                                </p>
                                <p className="text-xs" style={{ color: mutedColor, margin: 0 }}>
                                    {mime === 'application/pdf' ? 'Klepni na Otevřít dokument.' : 'Stáhni ho a otevři ve vhodné aplikaci.'}
                                </p>
                            </SheetKarta>
                        )}
                    </SheetModal>
                );
            })()}

            {/* Sekce na mobilu nepřepíná lišta nahoře, ale střed mobilní
                lišty — podržet a přejet, nebo klepnout. Na desktopu jsou
                všechny sekce vidět najednou v gridu. Horní odsazení jen u sekcí
                s obsahem pod hlavičkou; cockpit Moje směna jde až k ní. */}
            {isMobile && !['moje-smena', 'notifikace'].includes(activeTab)
                && <div aria-hidden="true" style={{ flexShrink: 0, height: 12 }} />}

            {/* Desktop: Já | Můj tým — Cockpit má pevnou mřížku bez volného
                místa, tým je proto celý druhý pohled (norma: seznam · detail ·
                třetí sloupec), ne další karta. */}
            {!isMobile && maTym && (
                <div role="tablist" aria-label="Pohled Cockpitu" className="flex items-center" style={{ flexShrink: 0, gap: 4, marginBottom: 12 }}>
                    {[{ id: 'ja', label: 'Já', Icon: UserRound }, { id: 'tym', label: 'Můj tým', Icon: Users }].map(p => {
                        const akt = deskPohled === p.id;
                        return (
                            <button key={p.id} type="button" role="tab" aria-selected={akt} onClick={() => setDeskPohled(p.id)}
                                className="inline-flex items-center gap-1.5 rounded-full"
                                style={{ height: 32, padding: '0 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                                    background: akt ? 'rgba(180,122,234,0.16)' : 'transparent', color: akt ? (isDark ? '#B47AEA' : '#831BE4') : 'var(--text-secondary)',
                                    border: `1px solid ${akt ? 'rgba(180,122,234,0.45)' : (isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)')}` }}>
                                <p.Icon size={14} /> {p.label}
                            </button>
                        );
                    })}
                </div>
            )}
            {!isMobile && maTym && deskPohled === 'tym' && (
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <MujTym user={user} isDark={isDark} isMobile={false} cardStyle={cardStyle} onNavigate={onNavigate} />
                </div>
            )}

            <div style={!isMobile ? {
                display: maTym && deskPohled === 'tym' ? 'none' : 'grid',
                /* Standardní třísloupcové rozvržení (jako Receptář — ModulLayout),
                   se stejnými šířkami sloupců:
                   - 1. sloupec (šířka seznamu) „Já": vizitka, stav nástupu,
                     úkoly, dokumenty — to, co mám udělat a co mám v papírech
                   - 2. sloupec (šířka detailu) „moje práce": kalendář nahoře, KPI
                     pod ním. Kalendář řídí měsíc, podle kterého se KPI počítají,
                     takže ovládání sedí nad tím, co ovládá; a příští směna je
                     první, co člověk vidí. Kalendář dostane víc výšky (3 : 2) —
                     týdnů je pět až šest, řádků dlaždic KPI jeden až dva
                   - 3. sloupec (šířka panelu) „můj rozvoj": kariéra a certifikace
                   Užší okno než PANEL_OD: třetí sloupec se neschová (jako panel
                   v modulech), rozvoj se přestěhuje do 1. sloupce pod „Já".
                   Moje směna se na desktopu nezobrazuje (je jen mobile tab). */
                gridTemplateColumns: triSloupce
                    ? `clamp(${SEZNAM_SIRKA.min}px, ${SEZNAM_SIRKA.ideal}, ${SEZNAM_SIRKA.max}px) minmax(0, 1fr) ${PANEL_SIRKA}px`
                    : `clamp(${SEZNAM_SIRKA.min}px, ${SEZNAM_SIRKA.ideal}, ${SEZNAM_SIRKA.max}px) minmax(0, 1fr)`,
                gridTemplateAreas: triSloupce
                    ? `"ja kalendar rozvoj" "ja kpi rozvoj"`
                    : `"ja kalendar" "rozvoj kpi"`,
                /* `minmax(0, …fr)` (ne `…fr`) je záměr: `fr` má auto minimum,
                   takže delší obsah karty řadu roztahoval a celý cockpit při
                   rozbalení (např. kvalifikačních cílů) poskočil. Teď má blok
                   pevnou výšku a přetečený obsah scrolluje uvnitř karty. */
                gridTemplateRows: triSloupce
                    ? 'minmax(0, 3fr) minmax(0, 2fr)'
                    : 'minmax(0, 3fr) minmax(0, 2fr)',
                gap: 16,
                alignItems: 'stretch',
                height: '100%',
                minHeight: 0,
            } : {
                /* Mobile: každá sekce vyplní prostor mezi hlavičkou a Palcem
                   bez scrollu stránky — kde je obsahu víc, scrolluje jen jeho
                   vnitřní část (dokumenty, kariéra…). Dlaždice z desktopu
                   tu nejsou, sekce jsou ploché. */
                flex: 1, minHeight: 0,
                display: 'flex', flexDirection: 'column',
                ...(!['moje-smena', 'notifikace'].includes(activeTab) ? { paddingBottom: REZERVA_LISTY } : {}),
                /* moje-smena: `visible` (ne `hidden`) — `overflowY: hidden` by
                   CSS povýšil `overflowX` na `auto` a ořízl edge-to-edge
                   breakout (−16px) cockpitu → boční „rámeček". Cockpit se
                   stejně vejde bez scrollu, takže visible je bezpečné. */
                overflowY: activeTab === 'moje-smena' ? 'visible' : 'hidden',
                /* Cockpit „Moje směna" — flush pod tab bar (žádná mezera),
                   cockpit si drží vlastní fázový gradient od horní hrany. */
                ...(activeTab === 'moje-smena' ? { marginTop: 0 } : {}),
            }}>
            {/* ===== SEKCE: DNEŠNÍ SMĚNA (cockpit bez kalendáře — ten je v „Můj měsíc") =====
                 JEN na mobilu jako jeden z tabů. Na desktopu se nezobrazuje —
                 prostor v gridu zabírá karta „Osobní dokumenty" (viz níže). */}
            {/* ===== SEKCE: NOTIFIKACE (mobilní sekce) ===== */}
            {isMobile && activeTab === 'notifikace' && (
                /* Seznam scrolluje přes celou výšku sekce: rezerva na lištu je
                   uvnitř (aby konec šel vytáhnout nad ni, ne aby se scroll
                   zkrátil) a horní hrana sahá až k výdechu, aby do něj obsah
                   doznil místo ostrého řezu. */
                <div style={{
                    flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
                    // Nadpis první skupiny stál v maskovaném pásu a byl pod
                    // výdechem špatně čitelný; odsazení ho z něj posune ven,
                    // přitom obsah při listování pořád doznívá.
                    paddingTop: 12,
                    paddingBottom: REZERVA_LISTY,
                    maskImage: 'linear-gradient(to bottom, transparent 0, #000 14px)',
                    WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, #000 14px)',
                }}>
                    {/* Jedna schránka nevyřízeného: notifikace i úkoly z „co mě
                        čeká" v jednom seznamu. Na mobilu se mezi sekcemi
                        přepíná, takže dvě schránky znamenaly, že člověk musí
                        vědět, že jsou dvě, a po vyřízení nevěděl, kde se to
                        odškrtává. Na desktopu jsou vidět naráz, tam zůstávají
                        ve sloupci „Já". */}
                    <Aktivita user={user} onNavigate={onNavigate} ukoly={actionItems.all} />
                </div>
            )}

            {isMobile && activeTab === 'moje-smena' && (
                <MojeSmena user={user} cardStyle={cardStyle} isDark={isDark} fillContainer onNavigate={onNavigate}
                    kalendar={false} onOtevritKalendar={() => setActiveTab('muj-mesic')} />
            )}

            {/* ===== SEKCE: OSOBNÍ / ROZVOJ / AKCE (3 samostatné karty)
                 Desktop = každá v gridu (řádek 2: osobni 1/3, rozvoj 1/3, akce 1/3)
                 Mobile = tab switcher přepíná jednu po druhé.
                 Onboarding banner se zobrazí jen na mobile uvnitř těchto tabů. */}

            {/* ===== SEKCE: MŮJ PROFIL (mobilní tab: vizitka, úkoly, dokumenty) =====
                 Jako sloupec „Já" na desktopu níže. */}
            {isMobile && activeTab === 'profil' && (() => {
                const celeJmeno = `${zamestnanec.titul_pred ? `${zamestnanec.titul_pred} ` : ''}${zamestnanec.jmeno} ${zamestnanec.prijmeni}${zamestnanec.titul_za ? `, ${zamestnanec.titul_za}` : ''}`;
                const podpis = [zamestnanec.typ_uvazku?.nazev, zamestnanec.oddeleni?.map(o => o.nazev).join(', ')].filter(Boolean).join(' · ');
                const adresa = [zamestnanec.adresa_ulice, zamestnanec.adresa_psc, zamestnanec.adresa_mesto].filter(Boolean).join(', ');
                const nadpis = (text) => (
                    <h4 className="text-[10px] uppercase" style={{ color: mutedColor, letterSpacing: '0.08em', fontWeight: 600 }}>{text}</h4>
                );
                const cip = {
                    display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%', minWidth: 0,
                    padding: '6px 11px', borderRadius: 999, fontSize: 13, textDecoration: 'none',
                    color: secondaryColor, border: `1px solid ${borderColor}`,
                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)',
                };
                return (
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {renderOnboardingBanner(true)}

                        {/* Vizitka */}
                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 14 }}>
                            {renderVizitkaFoto(76, 'var(--radius-2xl, 10px)')}
                            <div className="min-w-0 flex-1">
                                <h3 className="text-xl font-bold leading-tight" style={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{celeJmeno}</h3>
                                <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1.5">
                                    {zamestnanec.pozice?.nazev && (
                                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{
                                            background: 'rgba(245,158,11,0.12)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.24)',
                                        }}>{zamestnanec.pozice.nazev}</span>
                                    )}
                                    {podpis && <span className="text-[12px]" style={{ color: mutedColor }}>{podpis}</span>}
                                </div>
                            </div>
                        </div>

                        {/* Kontakty */}
                        <div style={{ flexShrink: 0, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {zamestnanec.telefon && (
                                <a href={`tel:${zamestnanec.telefon.replace(/\s+/g, '')}`} style={cip}>
                                    <Phone size={13} style={{ color: mutedColor, flexShrink: 0 }} />{zamestnanec.telefon}
                                </a>
                            )}
                            {zamestnanec.email && (
                                <a href={`mailto:${zamestnanec.email}`} style={cip}>
                                    <Mail size={13} style={{ color: mutedColor, flexShrink: 0 }} /><span className="truncate">{zamestnanec.email}</span>
                                </a>
                            )}
                            {zamestnanec.kontaktni_email && zamestnanec.kontaktni_email !== zamestnanec.email && (
                                <a href={`mailto:${zamestnanec.kontaktni_email}`} style={cip}>
                                    <Mail size={13} style={{ color: mutedColor, flexShrink: 0 }} /><span className="truncate">{zamestnanec.kontaktni_email}</span>
                                </a>
                            )}
                            {adresa && (
                                <span style={cip}>
                                    <MapPin size={13} style={{ color: mutedColor, flexShrink: 0 }} /><span className="truncate">{adresa}</span>
                                </span>
                            )}
                        </div>

                        {/* Úkoly tu bývaly taky. Od sloučení schránek je nese
                            sekce Notifikace — na mobilu se mezi sekcemi
                            přepíná, takže dvě místa se stejným seznamem
                            znamenala, že po vyřízení nevíš, kde se odškrtává. */}

                        {/* Dokumenty — zbytek výšky, scroll uvnitř */}
                        <div style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 14, borderTop: `1px solid ${borderColor}` }}>
                            <div className="shrink-0">{nadpis('Osobní dokumenty')}</div>
                            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                                <PersonalDocsList zamestnanec={zamestnanec} isDark={isDark} mutedColor={mutedColor} openDokumentPreview={openDokumentPreview} seznam />
                            </div>
                        </div>

                        {/* Vzhled a odhlášení — věci „ke mně". Bydlely v listu
                            účtu pod menu modulů, kde je nikdo nehledal: ta
                            obrazovka se jmenuje Moduly. Odhlášení je u spodní
                            hrany, kam dosáhne palec, a odděleně od zbytku —
                            na sdíleném zařízení se jím střídají směny. */}
                        <div style={{ flexShrink: 0, paddingTop: 14, borderTop: `1px solid ${borderColor}` }}>
                            {nadpis('Vzhled')}
                            <div style={{
                                display: 'flex', gap: 2, marginTop: 8, padding: 3, borderRadius: 10,
                                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                            }}>
                                {MOTIVY.map((m) => (
                                    <button key={m.v} type="button" onClick={() => setTheme(m.v)}
                                        aria-pressed={theme === m.v}
                                        style={{
                                            flex: 1, height: 42, borderRadius: 8, border: 'none', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                            font: 'inherit', fontSize: 13, fontWeight: theme === m.v ? 600 : 500,
                                            background: theme === m.v ? 'var(--brand-bg)' : 'transparent',
                                            color: theme === m.v ? 'var(--brand-text)' : 'var(--text-secondary)',
                                        }}>
                                        <m.Icon size={14} />
                                        {m.label}
                                    </button>
                                ))}
                            </div>

                            <button type="button" onClick={() => setZarizeniOpen(true)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                                    minHeight: 48, marginTop: 12, padding: '0 12px', borderRadius: 12,
                                    border: 'none', cursor: 'pointer', font: 'inherit',
                                    fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', textAlign: 'left',
                                    background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                                }}>
                                <span aria-hidden="true" style={{
                                    width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                }}>
                                    <Laptop size={16} />
                                </span>
                                <span style={{ flex: 1 }}>Přihlášená zařízení</span>
                                <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                            </button>
                            {zarizeniOpen && (
                                <ModalShell onClose={() => setZarizeniOpen(false)} isDark={isDark} nadpis="Přihlášená zařízení" ikona={Laptop} barva="#6B7280">
                                    <div style={{ padding: 16 }}><PrihlasenaZarizeni isDark={isDark} /></div>
                                </ModalShell>
                            )}

                            <button type="button" onClick={() => onLogout?.()}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                                    minHeight: 48, marginTop: 12, padding: '0 12px', borderRadius: 12,
                                    border: 'none', cursor: 'pointer', font: 'inherit',
                                    fontSize: 14, fontWeight: 600, color: '#EF4444', textAlign: 'left',
                                    background: 'rgba(239,68,68,0.10)',
                                }}>
                                <span aria-hidden="true" style={{
                                    width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: 'rgba(239,68,68,0.16)',
                                }}>
                                    <LogOut size={16} />
                                </span>
                                Odhlásit se
                            </button>

                        </div>
                    </div>
                );
            })()}

            {/* ===== SEKCE: JÁ (desktop — levý sloupec přes celou výšku) =====
                 Kompaktní vizitka, stav nástupu, úkoly a dokumenty v jedné kartě.
                 Úkoly jsou skoro vždy dokumenty k podpisu a směrnice, takže patří
                 k dokumentům. Bez úkolů zaberou jeden řádek a místo dostanou
                 dokumenty — hlavní místo stránky tak nezůstane prázdné. */}
            {!isMobile && (
            <div style={deskCell('ja')}>
                <div ref={jaRef} className="rounded-xl" style={{ ...cardStyle, height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {(() => {
                        const celeJmeno = `${zamestnanec.titul_pred ? `${zamestnanec.titul_pred} ` : ''}${zamestnanec.jmeno} ${zamestnanec.prijmeni}${zamestnanec.titul_za ? `, ${zamestnanec.titul_za}` : ''}`;
                        const podpis = [zamestnanec.typ_uvazku?.nazev, zamestnanec.oddeleni?.map(o => o.nazev).join(', ')].filter(Boolean).join(' · ');
                        // Kontaktní e-mail a adresa nejsou na očích — jsou v tooltipu kontaktů.
                        const kontaktyTitle = [
                            zamestnanec.email,
                            zamestnanec.kontaktni_email && zamestnanec.kontaktni_email !== zamestnanec.email ? zamestnanec.kontaktni_email : null,
                            [zamestnanec.adresa_ulice, zamestnanec.adresa_psc, zamestnanec.adresa_mesto].filter(Boolean).join(', ') || null,
                        ].filter(Boolean).join('\n');
                        const poziceBadge = zamestnanec.pozice?.nazev && (
                            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0" style={{
                                background: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(245,158,11,0.10)',
                                color: '#F59E0B', border: '1px solid rgba(245,158,11,0.24)',
                            }}>{zamestnanec.pozice.nazev}</span>
                        );

                        // Nízká karta (menší okno): vizitka v jednom řádku, ať zbyde
                        // místo na úkoly a dokumenty.
                        if (jaRozmer.h > 0 && jaRozmer.h < 720) {
                            return (
                                <div className="flex items-center gap-3 px-5 pt-5 pb-4" style={{ borderBottom: `1px solid ${borderColor}`, flexShrink: 0 }}>
                                    {renderVizitkaFoto(64, 'var(--radius-2xl, 10px)')}
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <h3 className="text-base font-bold truncate" style={{ color: 'var(--text-primary)' }}>{celeJmeno}</h3>
                                            {poziceBadge}
                                        </div>
                                        <p className="text-[11px] truncate mt-0.5" style={{ color: mutedColor }}>{podpis || '—'}</p>
                                        <div className="flex items-center gap-3 mt-1 text-[11px] min-w-0" style={{ color: secondaryColor }} title={kontaktyTitle}>
                                            {zamestnanec.telefon && (
                                                <span className="flex items-center gap-1 shrink-0"><Phone size={11} style={{ color: mutedColor }} />{zamestnanec.telefon}</span>
                                            )}
                                            {zamestnanec.email && (
                                                <span className="flex items-center gap-1 min-w-0"><Mail size={11} style={{ color: mutedColor, flexShrink: 0 }} /><span className="truncate">{zamestnanec.email}</span></span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        // Vysoká karta: vizitka vycentrovaná, fotka roste s výškou karty.
                        const foto = Math.round(Math.max(76, Math.min(116, (jaRozmer.h || 800) * 0.11)));
                        const vzdusne = !jaRozmer.h || jaRozmer.h >= 860;
                        const pilulka = {
                            display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%',
                            padding: '4px 10px', borderRadius: 999, fontSize: 11.5,
                            color: secondaryColor, textDecoration: 'none',
                            background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)',
                            border: `1px solid ${borderColor}`,
                        };
                        return (
                            <div className={vzdusne ? 'px-5 pt-7 pb-5' : 'px-5 pt-5 pb-4'} style={{
                                flexShrink: 0, borderBottom: `1px solid ${borderColor}`,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: vzdusne ? 14 : 10,
                                // Jemná záře značky za fotkou — vizitka je první, co na Přehledu vidím.
                                background: isDark
                                    ? 'radial-gradient(120% 90% at 50% 0%, rgba(255,138,0,0.16) 0%, rgba(151,90,205,0.10) 45%, transparent 75%)'
                                    : 'radial-gradient(120% 90% at 50% 0%, rgba(255,138,0,0.14) 0%, rgba(151,90,205,0.08) 45%, transparent 75%)',
                            }}>
                                {renderVizitkaFoto(foto, 'var(--radius-3xl, 14px)')}
                                <div className="min-w-0 w-full">
                                    <h3 className="text-lg font-bold leading-tight" style={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{celeJmeno}</h3>
                                    {(poziceBadge || podpis) && (
                                        <div className="flex items-center justify-center flex-wrap gap-x-2 gap-y-1 mt-2">
                                            {poziceBadge}
                                            {podpis && <span className="text-[11.5px]" style={{ color: mutedColor }}>{podpis}</span>}
                                        </div>
                                    )}
                                </div>
                                {(zamestnanec.telefon || zamestnanec.email) && (
                                    <div className="flex items-center justify-center flex-wrap gap-1.5 w-full min-w-0" title={kontaktyTitle}>
                                        {zamestnanec.telefon && (
                                            <a href={`tel:${zamestnanec.telefon.replace(/\s+/g, '')}`} style={{ ...pilulka, flexShrink: 0 }}>
                                                <Phone size={12} style={{ color: mutedColor, flexShrink: 0 }} />{zamestnanec.telefon}
                                            </a>
                                        )}
                                        {zamestnanec.email && (
                                            <a href={`mailto:${zamestnanec.email}`} style={{ ...pilulka, minWidth: 0 }}>
                                                <Mail size={12} style={{ color: mutedColor, flexShrink: 0 }} /><span className="truncate">{zamestnanec.email}</span>
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Úkoly — obsah určuje výšku, nejvýš polovina karty, pak scroll */}
                    <div className="px-5 pt-4 pb-4" style={{ flex: '0 1 auto', maxHeight: '50%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {renderOnboardingBanner(true)}
                        <div className="flex items-center justify-between shrink-0">
                            <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: secondaryColor }}>
                                <Rocket size={14} /> Úkoly
                            </h3>
                            {actionItems.all.length > 0 && (
                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                                    style={{ background: 'rgba(249,115,22,0.12)', color: '#f97316', border: '1px solid rgba(249,115,22,0.25)' }}>
                                    {actionItems.all.length}
                                </span>
                            )}
                        </div>
                        {(nepodepsaneLoading || skoleniLoading || docsKPodpisuLoading) ? (
                            <p className="text-xs" style={{ color: mutedColor }}>Načítám…</p>
                        ) : actionItems.all.length === 0 ? (
                            <p className="text-xs flex items-center gap-1.5" style={{ color: '#10b981' }}>
                                <CheckCircle2 size={14} /> Žádné úkoly, vše splněno
                            </p>
                        ) : (
                            <div className="space-y-1.5" style={{ minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                                {renderAkceItems(actionItems.all)}
                            </div>
                        )}
                    </div>

                    {/* Dokumenty — zbytek výšky */}
                    <div className="px-5 pt-4 pb-5" style={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', borderTop: `1px solid ${borderColor}` }}>
                        <h3 className="text-sm font-semibold flex items-center gap-2 mb-3" style={{ color: secondaryColor }}>
                            <FileText size={14} /> Moje dokumenty
                        </h3>
                        <PersonalDocsList zamestnanec={zamestnanec} isDark={isDark} mutedColor={mutedColor} openDokumentPreview={openDokumentPreview} seznam />
                    </div>
                </div>
            </div>
            )}

            {/* ===== SEKCE: ROZVOJ (Kariéra + Certifikace) =====
                 Desktop = třetí sloupec (šířka panelu), kariéra nad certifikacemi,
                 každá část scrolluje sama. Mobile = pod sebou v tabu „Rozvoj". */}
            {showSection('rozvoj') && (
            <div style={isMobile ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : deskCell('rozvoj')}>
                    <div className={isMobile ? '' : 'rounded-xl p-5'} style={isMobile
                        ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
                        : { ...deskCard, overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        {/* ── Header s alert badge ── */}
                        {(() => {
                            const problemCount = certifikatyKReseni;
                            // Mobil: název sekce nese hlavička — v kartě jen upozornění, když je co řešit
                            if (isMobile && problemCount === 0) return null;
                            return (
                                <div className="flex items-center justify-between mb-4">
                                    {!isMobile && (
                                        <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: secondaryColor }}>
                                            <TrendingUp size={14} /> Osobní rozvoj
                                        </h3>
                                    )}
                                    {problemCount > 0 && (
                                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                            style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.25)' }}>
                                            {problemCount} vyžadují pozornost
                                        </span>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Desktop: kariéra nad certifikacemi. Obě části se zmenšují podle
                            obsahu a scrollují samy — volné místo zůstane dole, ne mezi nimi. */}
                        {/* Mobil: celá sekce scrolluje jako jeden sloupec a nahoře je
                            Moje vzdělání — obsluha nemá modul Vzdělávání, tady vidí, co
                            má udělat a co už absolvovala. */}
                        <div style={isMobile
                            ? { flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }
                            : { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        {/* Moje vzdělání je od 2026-09-28 jediné místo, kde člověk vidí svá
                            školení (záložka v modulu zanikla) — proto i na desktopu, nad kariérou. */}
                        {isMobile
                            ? <MojeVzdelani skoleni={skoleniData} isDark={isDark} onNavigate={onNavigate} />
                            : (
                                <div style={{ flex: '0 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                                    <MojeVzdelani skoleni={skoleniData} isDark={isDark} onNavigate={onNavigate} />
                                </div>
                            )}
                        {/* ── Sekce: Kariéra ── */}
                        <div style={{ flex: '0 1 auto', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain' }}>
                        <p className="text-[10px] uppercase mb-2" style={{ color: mutedColor, letterSpacing: '0.08em', fontWeight: 500, opacity: 0.7 }}>Kariéra</p>
                        {karieraCards.length > 0 ? (
                            <div className="space-y-3">
                                {karieraCards.map((kc, ki) => {
                                    const currentPoradi = kc.currentUroven?.poradi ?? 0;
                                    const isMaxLevel = currentPoradi === (kc.allUrovne.length - 1);
                                    const progressBarColor = kc.cilPct >= 100 ? '#34d399' : kc.cilPct >= 60 ? '#fbbf24' : '#f87171';
                                    // Desktop: rozvoj má vlastní sloupec na výšku, takže jsou všechny
                                    // kariéry rozbalené rovnou. Akordeon zůstává jen na mobilu.
                                    const isExpanded = isMobile ? expandedKariera === ki : true;
                                    // Cíl je stejná (nebo nižší) úroveň, než kterou už člověk má — dřív
                                    // se kreslilo „Medior → Medior 100 %" a nad hotovými dovednostmi
                                    // „Chybí dokončit". Stejně matoucí bylo „Chybí dokončit" u cíle,
                                    // kde je vše splněné a čeká se jen na povýšení.
                                    const cilPoradi = kc.cilUroven ? (kc.allUrovne.find(ul => ul.id === kc.cilUroven.id)?.poradi ?? kc.cilUroven.poradi) : null;
                                    const cilSplnen = !isMaxLevel && cilPoradi != null && cilPoradi <= currentPoradi;
                                    const vseHotovo = kc.cilTotal > 0 && kc.cilDone >= kc.cilTotal;
                                    const sekceGrid = (popisek) => Object.keys(kc.sekceBySekceId).length > 0 && (
                                        <div>
                                            <p className="text-[11px] font-semibold mb-2" style={{ color: mutedColor }}>{popisek}</p>
                                            <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                                {Object.entries(kc.sekceBySekceId).map(([sekceId, sekce]) => {
                                                    const total = sekce.dovednosti.length;
                                                    const done = sekce.dovednosti.filter(d => d.done).length;
                                                    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                                                    const barColor = done >= total ? '#34d399' : done > 0 ? '#fbbf24' : '#f87171';
                                                    const hoverKey = `${ki}-${sekceId}`;
                                                    return (
                                                        <div key={sekceId} className="rounded-lg overflow-hidden cursor-default"
                                                            style={{ background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }}
                                                            onMouseEnter={(e) => {
                                                                const rect = e.currentTarget.getBoundingClientRect();
                                                                setHoveredSekce({ key: hoverKey, rect, sekce });
                                                            }}
                                                            onMouseLeave={() => setHoveredSekce(null)}>
                                                            <div className="flex items-center gap-2 px-2.5 py-1.5">
                                                                <span className="text-[11px] flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{sekce.nazev}</span>
                                                                <span className="text-[11px] font-bold shrink-0" style={{ color: barColor }}>{done}/{total}</span>
                                                            </div>
                                                            <div className="h-1" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
                                                                <div className="h-full transition-all" style={{ width: `${pct}%`, background: barColor, borderRadius: pct < 100 ? '0 2px 2px 0' : 0 }} />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                    return (
                                        <div key={ki} className="rounded-xl border" style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'var(--surface-elevated)', borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)' }}>
                                            <div className={`flex justify-between items-center px-4 py-3 ${isMobile ? 'cursor-pointer select-none' : ''}`} onClick={isMobile ? () => setExpandedKariera(isExpanded ? null : ki) : undefined}>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="text-sm font-bold truncate" style={{ color: 'var(--text-primary)' }}>{kc.kariera?.nazev}</span>
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${karieraUrovenColors[kc.currentUroven?.nazev] || 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}>{kc.currentUroven?.nazev}</span>
                                                    {!kc.certOk && <AlertTriangle size={13} className="text-red-400 shrink-0" title="Certifikace neplatná" />}
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0 ml-2">
                                                    {!isExpanded && cilSplnen && (
                                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', color: '#34d399' }}>Cíl splněn</span>
                                                    )}
                                                    {!isExpanded && !cilSplnen && kc.cilPct !== null && !isMaxLevel && (
                                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: kc.cilPct >= 100 ? 'rgba(52,211,153,0.12)' : kc.cilPct >= 60 ? 'rgba(251,191,36,0.12)' : 'rgba(248,113,113,0.12)', color: kc.cilPct >= 100 ? '#34d399' : kc.cilPct >= 60 ? '#fbbf24' : '#f87171' }}>{kc.cilPct}%</span>
                                                    )}
                                                    {!isExpanded && isMaxLevel && (
                                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', color: '#34d399' }}>Dosažen</span>
                                                    )}
                                                    {isMobile && <ChevronDown size={14} style={{ color: mutedColor, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
                                                </div>
                                            </div>
                                            {isExpanded && <div className="px-4 pb-4" style={{ borderTop: `1px solid ${borderColor}` }}>
                                            {isMaxLevel ? (
                                                <div className="flex items-center justify-center py-4">
                                                    <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', color: '#34d399' }}>Nejvyšší úroveň dosažena</span>
                                                </div>
                                            ) : cilSplnen ? (<>
                                                <div className="flex flex-col items-center gap-1 pt-4 pb-3">
                                                    <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', color: '#34d399' }}>Cílová úroveň {kc.cilUroven?.nazev} splněna</span>
                                                    <span className="text-[10px]" style={{ color: mutedColor }}>Další cíl nastaví manažer</span>
                                                </div>
                                                {sekceGrid(`Dovednosti úrovně ${kc.cilUroven?.nazev}:`)}
                                            </>) : kc.cilUroven ? (<>
                                                <div className="flex items-center pt-3 mb-3">
                                                    <div className="flex items-center gap-2 text-sm">
                                                        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${karieraUrovenColors[kc.currentUroven?.nazev] || 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}>{kc.currentUroven?.nazev}</span>
                                                        <span style={{ color: mutedColor }}>→</span>
                                                        <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{kc.cilUroven?.nazev}</span>
                                                    </div>
                                                </div>
                                                <div className="mb-4">
                                                    <div className="flex items-baseline justify-between mb-1.5">
                                                        <span className="text-xs" style={{ color: secondaryColor }}>Postup do další úrovně:</span>
                                                        <span className="text-sm font-bold" style={{ color: progressBarColor }}>{kc.cilPct} %</span>
                                                    </div>
                                                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)' }}>
                                                        <div className="h-full rounded-full transition-all" style={{
                                                            width: `${Math.min(kc.cilPct, 100)}%`,
                                                            background: kc.cilPct >= 80 ? 'linear-gradient(90deg, #34d399, #10b981)' : kc.cilPct >= 40 ? 'linear-gradient(90deg, #34d399, #fbbf24)' : 'linear-gradient(90deg, #f87171, #fbbf24)',
                                                        }} />
                                                    </div>
                                                    <div className="flex justify-between mt-1">
                                                        <span className="text-[10px]" style={{ color: mutedColor }}>{kc.currentUroven?.nazev}</span>
                                                        <span className="text-[10px]" style={{ color: mutedColor }}>{kc.cilUroven?.nazev}</span>
                                                    </div>
                                                </div>
                                                {sekceGrid(vseHotovo ? 'Vše splněno, čeká se na povýšení:' : 'Chybí dokončit:')}
                                            </>) : (
                                                <div className="pt-3">
                                                    <span className="text-xs" style={{ color: mutedColor }}>Žádný kariérní cíl nastaven</span>
                                                </div>
                                            )}
                                        </div>}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-xs" style={{ color: mutedColor }}>Žádné kariéry</p>
                        )}

                        </div>

                        {/* ── Sekce: Certifikace (vertikální list) ── */}
                        <div className="pt-5 mt-5" style={{ flex: '0 1 auto', maxHeight: '50%', minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', borderTop: `1px solid ${borderColor}` }}>
                            <p className="text-[10px] uppercase mb-3" style={{ color: mutedColor, letterSpacing: '0.08em', fontWeight: 500, opacity: 0.7 }}>Certifikace</p>
                            {(() => {
                                const requiredCerts = [...new Set(karieraCards.flatMap(kc => kc.kariera?.pozadovane_certifikace || []))];
                                if (requiredCerts.length === 0) return <p className="text-xs" style={{ color: mutedColor }}>Žádné požadované certifikace</p>;
                                /* Mapování cert kód → průběh školení (pro odkaz na školení získávající cert).
                                   Preferuje aktivní průběhy (neúspěšné → rozpracované → přiřazené → dokončené). */
                                const skoleniPrio = { neuspesne: 0, zahajeno: 1, prirazeno: 2, dokonceno: 3 };
                                const skoleniByCert = {};
                                (skoleniData || []).forEach(p => {
                                    const kod = p.skoleni?.certifikace_typ_kod;
                                    if (!kod) return;
                                    const prev = skoleniByCert[kod];
                                    if (!prev || (skoleniPrio[p.stav] ?? 9) < (skoleniPrio[prev.stav] ?? 9)) {
                                        skoleniByCert[kod] = p;
                                    }
                                });
                                /* SVG ikony — 20px, rozlišitelné tvary per typ */
                                const certSvgMap = {
                                    /* HACCP — štít s fajfkou (hygiena/potraviny) */
                                    HACCP: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>,
                                    /* BOZP — helma s křížem (bezpečnost práce) */
                                    BOZP: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M2 18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1H3a1 1 0 0 0-1 1v2z"/><path d="M10 15V7a2 2 0 0 1 4 0v8"/><path d="M4 15v-3a8 8 0 0 1 16 0v3"/><line x1="12" y1="4" x2="12" y2="2"/></svg>,
                                    /* Řidičský průkaz — volant (rozvoz) */
                                    RIDICAK_REF: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="3" x2="12" y2="9"/><line x1="4.2" y1="16.5" x2="9.4" y2="13.5"/><line x1="19.8" y1="16.5" x2="14.6" y2="13.5"/></svg>,
                                };
                                /* Fallback — medaile */
                                const certSvgFallback = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>;
                                const stavCfg = {
                                    ok:         { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  label: 'Platná' },
                                    brzy:       { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', label: 'Brzy vyprší' },
                                    expirovano: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)',  label: 'Expirovaná' },
                                    chybi:      { color: '#6b7280', bg: 'rgba(107,114,128,0.1)', label: 'Chybí' },
                                };
                                return (
                                    <div className="space-y-2">
                                        {requiredCerts.map(typ => {
                                            const cert = zamestnanec.certifikace?.find(c => c.typ === typ);
                                            const certDef = certTypy.find(ct => ct.kod === typ);
                                            const now = new Date();
                                            const platnostDo = cert ? new Date(cert.platnost_do) : null;
                                            const stav = cert
                                                ? (platnostDo < now ? 'expirovano' : platnostDo < new Date(Date.now() + 30 * 86400000) ? 'brzy' : 'ok')
                                                : 'chybi';
                                            const sc = stavCfg[stav];
                                            const daysLeft = platnostDo ? Math.round((platnostDo - now) / 86400000) : null;
                                            const subText = stav === 'ok' ? `do ${platnostDo.toLocaleDateString('cs-CZ')}`
                                                : stav === 'brzy' ? `vyprší ${platnostDo.toLocaleDateString('cs-CZ')}`
                                                : stav === 'expirovano' ? `expirováno ${platnostDo.toLocaleDateString('cs-CZ')}`
                                                : 'Zatím nezískaná';
                                            const badgeText = stav === 'brzy' && daysLeft !== null ? `${daysLeft} dní` : sc.label;
                                            const skol = skoleniByCert[typ];
                                            return (
                                                <div key={typ} className="flex items-center gap-3 rounded-lg px-3 py-2" style={{ background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                                                    <div className="flex items-center justify-center shrink-0" style={{ width: 32, height: 32, borderRadius: 6, background: sc.bg, color: sc.color }}>
                                                        {certSvgMap[typ] || certSvgFallback}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{certDef?.nazev || typ}</div>
                                                        <div className="text-[10px]" style={{ color: mutedColor }}>{subText}</div>
                                                    </div>
                                                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0" style={{ background: sc.bg, color: sc.color }}>{badgeText}</span>
                                                    {skol && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onNavigate?.('moje-skoleni-detail', { skoleniId: skol.id })}
                                                            title={`Otevřít školení: ${skol.skoleni?.nazev || ''}`}
                                                            className="flex items-center justify-center shrink-0 rounded-md transition-colors"
                                                            style={{ width: 24, height: 24, color: mutedColor, background: 'transparent', border: 'none', cursor: 'pointer' }}
                                                            onMouseEnter={e => { e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = mutedColor; }}
                                                        >
                                                            <BookOpen size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>
                        </div>

                    </div>
            </div>
            )}

            {/* ===== SEKCE: KALENDÁŘ MÝCH SMĚN (desktop, 2. sloupec nahoře) =====
                 Na mobilu je tentýž kalendář v sekci „Můj měsíc" níže. */}
            {!isMobile && (
                <div style={deskCell('kalendar')}>
                    <KalendarSmen isDark={isDark} cardStyle={cardStyle} kurzor={kalendarMesic} onKurzor={setKalendarMesic} datum={navParams?.datum ?? null} />
                </div>
            )}

            {/* ===== SEKCE: KPI DASHBOARD (desktop, 2. sloupec pod kalendářem, za jeho měsíc) =====
                 Desktop = živá KPI dle kariér (stejná sada jako zaměstnanecká appka,
                 /api/me/kpi/prehled). Mobilní web zůstává zatím u placeholderu —
                 na telefonu KPI žijí v FofrLamaApp. */}
            {!isMobile && (
                <div style={deskCell('kpi')}>
                    <MojeKpi isDark={isDark} cardStyle={cardStyle} kariery={zamestnanec?.kariery}
                        obdobi={obdobiKalendare} />
                </div>
            )}
            {/* ===== SEKCE: DOKUMENTACE (mobilní tab) =====
                 Řízená dokumentace — směrnice přiřazené mně a recepty podle
                 kariéry. Podstránky nese adresa, takže Zpět vrací z detailu. */}

            {/* ===== SEKCE: MŮJ MĚSÍC (mobilní tab) =====
                 Jako prostřední sloupec desktopu: kalendář směn (listování
                 měsíci, klik na den = detail směny s kolegy) a pod ním KPI
                 za měsíc otevřený v kalendáři. Obojí ve skle. */}
            {/* ===== SEKCE: MŮJ TÝM (mobil, jen vedoucí) ===== */}
            {isMobile && maTym && activeTab === 'muj-tym' && (
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <MujTym user={user} isDark={isDark} isMobile cardStyle={cardStyle} onNavigate={onNavigate} />
                </div>
            )}
            {isMobile && activeTab === 'muj-mesic' && (
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <KalendarSmen isDark={isDark} cardStyle={skloMesic} kurzor={kalendarMesic} onKurzor={setKalendarMesic} mobil datum={navParams?.datum ?? null} />
                    </div>
                    <div style={{ flex: '0 0 32%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <MojeKpi isDark={isDark} cardStyle={skloMesic} kariery={zamestnanec?.kariery} obdobi={obdobiKalendare} mobil />
                    </div>
                </div>
            )}
            </div>

            {/* Hover tooltip for skill sections — rendered via portal to avoid clipping */}
            {hoveredSekce && createPortal(
                <div style={{ position: 'fixed', zIndex: 99999, pointerEvents: 'none',
                    left: hoveredSekce.rect.left + hoveredSekce.rect.width / 2,
                    transform: 'translateX(-50%)',
                }} ref={el => {
                    if (!el) return;
                    const tooltipH = el.offsetHeight;
                    const { rect } = hoveredSekce;
                    if (rect.top > tooltipH + 10) {
                        el.style.top = `${rect.top - tooltipH - 6}px`;
                    } else {
                        el.style.top = `${rect.bottom + 6}px`;
                    }
                }}>
                    <div className="px-3 py-2.5 min-w-[180px] max-w-[260px]" style={MODAL_SKLO}>
                        <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-primary)' }}>{hoveredSekce.sekce.nazev}</div>
                        <div className="space-y-1">
                            {hoveredSekce.sekce.dovednosti.map(d => {
                                const dColor = d.done ? '#34d399' : d.uroven >= 2 ? '#fbbf24' : d.uroven >= 1 ? '#f87171' : '#6b7280';
                                return (
                                    <div key={d.kod} className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: dColor }} />
                                        <span className="text-[11px] flex-1" style={{ color: 'var(--text-secondary)' }}>{d.nazev}</span>
                                        <span className="text-[10px] font-semibold shrink-0" style={{ color: dColor }}>{urovenLabels[d.uroven] || '—'} / {urovenLabels[d.requiredUroven] || '—'}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

